import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Notification } from "../hooks/useNotifications";

interface Props {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onRemove: (id: string) => void;
  /**
   * Called after activation to close the parent UI (e.g. the dropdown).
   * Optional because this component is also rendered standalone on the
   * /notifications page, where there's nothing to close.
   */
  onClose?: () => void;
}

/**
 * Single row in the notifications dropdown / page.
 *
 * MOBILE NOTES (see commit history): uses onPointerUp + onClick with
 * a double-fire guard, touch-action: manipulation, and pointer-events:
 * none on inner visuals so taps reliably land on the activatable parent.
 */
const NotificationItem = ({
  notification,
  onMarkRead,
  onRemove,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const actorName =
    notification.actor?.username || t("common.someone", "alguien");
  const battleTheme = notification.battle?.theme || "";

  const getMessage = (): string => {
    switch (notification.type) {
      case "welcome":
        return t("notifications.welcome");
      case "like":
        return t("notifications.likedYourSketch", { username: actorName });
      case "comment":
        return t("notifications.commentedYourSketch", { username: actorName });
      case "comment_reply":
        return t("notifications.commentedSameSketch", { username: actorName });
      case "battle_voting":
        return t("notifications.battleVotingStarted", { theme: battleTheme });
      case "battle_finished":
        return t("notifications.battleFinished", { theme: battleTheme });
      case "battle_winner_popular":
        return t("notifications.battleWinnerPopular", { theme: battleTheme });
      case "battle_winner_jury":
        return t("notifications.battleWinnerJury", { theme: battleTheme });
      default:
        return "";
    }
  };

  const getLink = (): { to: string; state?: any } => {
    if (notification.type?.startsWith("battle_")) {
      return { to: "/actualbattle" };
    }
    if (notification.type === "welcome") {
      return { to: "/mysketchs", state: { openUpload: true } };
    }
    if (notification.sketch?._id) {
      return { to: `/sketchdetail/${notification.sketch._id}` };
    }
    return { to: "/sketches" };
  };

  const isWinNotification =
    notification.type === "battle_winner_popular" ||
    notification.type === "battle_winner_jury";
  const accent = isWinNotification ? "#39ff14" : "#ffcc00";

  const activate = (e: React.SyntheticEvent) => {
    const target = e.currentTarget as HTMLElement;
    if (target.dataset.handled === "1") return;
    target.dataset.handled = "1";
    setTimeout(() => {
      target.dataset.handled = "";
    }, 500);

    e.stopPropagation();

    if (!notification.read) onMarkRead(notification._id);
    const link = getLink();
    navigate(link.to, link.state ? { state: link.state } : undefined);
    // Only close if a closer was provided (dropdown context).
    // On the /notifications page this component is rendered standalone
    // and there's nothing to close.
    if (typeof onClose === "function") onClose();
  };

  const handleRemove = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(notification._id);
  };

  return (
    <div
      onPointerUp={activate}
      onClick={activate}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") activate(e);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        padding: "0.65rem 0.9rem",
        borderBottom: "1px solid #2a2a2a",
        textDecoration: "none",
        color: "#f0f0f0",
        backgroundColor: notification.read ? "transparent" : "#222",
        borderLeft: notification.read
          ? "3px solid transparent"
          : `3px solid ${accent}`,
        position: "relative",
        transition: "background-color 0.15s ease",
        cursor: "pointer",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        userSelect: "none",
      }}
    >
      {notification.actor?.avatar && (
        <img
          src={notification.actor.avatar}
          alt=""
          style={{
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
            pointerEvents: "none",
          }}
        />
      )}
      {!notification.actor && notification.sketch?.url && (
        <img
          src={notification.sketch.url}
          alt=""
          style={{
            width: "2.5rem",
            height: "2.5rem",
            objectFit: "cover",
            flexShrink: 0,
            border: `1px solid ${accent}`,
            pointerEvents: "none",
          }}
        />
      )}
      {!notification.actor && !notification.sketch?.url && (
        <span style={{ fontSize: "1.6rem", flexShrink: 0, pointerEvents: "none" }}>
          {isWinNotification ? "🏆" : "⚔"}
        </span>
      )}

      <div style={{ flex: 1, minWidth: 0, pointerEvents: "none" }}>
        <div
          style={{
            fontSize: "0.85rem",
            lineHeight: 1.3,
            color: "#f0f0f0",
            wordBreak: "break-word",
          }}
        >
          {getMessage()}
        </div>
        <div
          style={{
            fontSize: "0.7rem",
            color: "#888",
            marginTop: "0.2rem",
          }}
        >
          {new Date(notification.createdAt).toLocaleString()}
        </div>
      </div>

      <button
        onPointerUp={handleRemove}
        onClick={handleRemove}
        title={t("notifications.remove")}
        aria-label={t("notifications.remove")}
        style={{
          background: "none",
          border: "none",
          color: "#666",
          cursor: "pointer",
          padding: "0.2rem 0.4rem",
          fontSize: "1rem",
          flexShrink: 0,
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        ✕
      </button>
    </div>
  );
};

export default NotificationItem;
